import { useId } from 'preact/hooks'
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Ban,
  CalendarClock,
  CalendarX,
  Check,
  CircleOff,
  Info,
  Loader2,
  Play,
  RefreshCcw,
} from 'lucide-preact'
import { isTerminalBlastState, type EmailBlastSendingDomainPlanDto, type EmailBlastViewDto } from '@/types/emailBlast'
import { BlastStateBadge } from '../BlastStateBadge'
import { RateMeter } from '../RateMeter'
import { SendingDomainStatePill } from '../SendingDomainStatePill'
import { buildBlastVerdict, buildPacingLine, latestTrip, type VerdictTone } from './blastVerdict'
import { BlastProgressBar } from './BlastProgressBar'
import { BroadcastDayPlan } from './BroadcastDayPlan'
import { BlastFreshness } from './BlastFreshness'
import {
  cancelledLateLine,
  domainDayTotal,
  forecastAssumption,
  planDomainState,
  stoppedDomainResumeNote,
} from './sendingDomainPlan'

interface Props {
  blast: EmailBlastViewDto
  /** How the broadcast's sending domain governs it (ORB-953); null until it loads or when it could not be read. */
  plan: EmailBlastSendingDomainPlanDto | null
  /** The sending domain's Settings page for a company admin, who alone can open it; null for everyone else. */
  domainHref: string | null
  subject: string | null
  loading: boolean
  actioning: boolean
  /** Why the API refused the last Resume, kept beside the button rather than in a toast. */
  resumeRefusal: string[] | null
  onRefresh: () => void
  onResume: () => void
  onCancel: () => void
}

const TONE_ICON: Record<VerdictTone, typeof Check> = {
  healthy: Check,
  attention: AlertTriangle,
  scheduled: CalendarClock,
  finished: Check,
  stopped: CircleOff,
}

const TONE_CLASS: Record<VerdictTone, string> = {
  healthy: 'text-primary-600 dark:text-primary-400',
  attention: 'text-amber-500 dark:text-amber-400',
  scheduled: 'text-blue-500 dark:text-blue-400',
  finished: 'text-emerald-600 dark:text-emerald-400',
  stopped: 'text-slate-400 dark:text-slate-500',
}

/**
 * The blast detail's lead block (ORB-706): the answer to "is this send healthy, and do I need to
 * intervene", before any of the evidence for it.
 *
 * The page used to open with two rate meters, which is evidence for a question nobody had been asked
 * yet — so the thing an operator opened the page to find out was the fourth thing on it. Order here is
 * DOM order and priority order both: what it is, the verdict, what you can do about it, how far
 * through, what might stop it, and how fresh the numbers are.
 *
 * Resume and Cancel live here rather than in the page header on purpose. A Resume button several
 * hundred pixels from the sentence explaining the pause is a decision made without its reason.
 */
export function BlastStatusSpine({
  blast,
  plan,
  domainHref,
  subject,
  loading,
  actioning,
  resumeRefusal,
  onRefresh,
  onResume,
  onCancel,
}: Props) {
  const resumeNoteId = useId()
  const verdict = buildBlastVerdict(blast, plan)
  const pacing = buildPacingLine(blast, verdict)
  const trip = latestTrip(blast)
  const isTerminal = isTerminalBlastState(blast.State)
  const canResume = blast.State === 'Paused'
  const canCancel = !isTerminal

  const domain = plan?.Domain ?? null
  const domainState = planDomainState(plan)
  // What the domain holds back is only news while the broadcast still has something to send.
  const domainStopped = !isTerminal && domainState === 'Stopped'
  const domainHeld = domainStopped || (!isTerminal && domainState === 'SettingUp')
  const resumeBlocked = canResume && domainStopped
  const multiDay = verdict.days.length > 0
  const dayTotal = multiDay && plan ? domainDayTotal(plan) : null
  const resumeNote = domainStopped && plan ? stoppedDomainResumeNote(plan, verdict.queued, canResume) : null
  const cancelledLate = plan ? cancelledLateLine(plan.CancelledLate) : null
  const step =
    domain && domainState === 'Warming' && domain.WarmupStage != null
      ? `Step ${domain.WarmupStage} of ${domain.WarmupLastStep}`
      : null

  const ToneIcon = TONE_ICON[verdict.tone]

  // The breaker judges the canary window, or the post-resume window on a resumed blast, while the
  // Delivery funnel below stays cumulative. Saying which keeps the two from looking like they disagree.
  const sampleCaption = blast.ResumedAtUtc
    ? `measured on sends since you resumed at ${new Date(blast.ResumedAtUtc).toLocaleTimeString()}`
    : blast.State === 'Measuring' || blast.State === 'Canary'
    ? `measured on the ${blast.CanarySize.toLocaleString()}-email test batch`
    : `measured on ${verdict.sent.toLocaleString()} sends`

  // Below the min-send floor a rate is noise: 0.00% on three sends is not information.
  const tooEarlyToJudge =
    !isTerminal && verdict.sent > 0 && verdict.sent < blast.MinCanarySendsForRelease

  // The engine evaluates this floor from AdvanceMeasuringAsync and nowhere else — during Canary and the
  // drain, delivery legitimately trails sends while webhooks are still arriving, so showing it live
  // would paint a red meter on a healthy send the engine is not even checking.
  const deliveryGateApplies = blast.State === 'Measuring' || trip?.TripMetric === 'DeliveryRate'

  return (
    <section className="space-y-4" aria-label="Send status">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {subject || 'Email blast'}
          </h2>
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
            <span>
              {verdict.notStartedYet ? 'Scheduled' : 'Started'} {new Date(blast.SendAtAbsoluteUtc).toLocaleString()}
            </span>
            <span aria-hidden="true">·</span>
            <span>{verdict.recipients.toLocaleString()} recipients</span>
            {domain && (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  from{' '}
                  {domainHref ? (
                    <a
                      href={domainHref}
                      className="text-slate-700 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-500 dark:text-slate-200 dark:decoration-slate-600"
                    >
                      {domain.Domain}
                    </a>
                  ) : (
                    <span className="text-slate-700 dark:text-slate-200">{domain.Domain}</span>
                  )}
                </span>
                <SendingDomainStatePill domain={domain} />
                {step && <span>{step}</span>}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <BlastStateBadge state={blast.State} presentAs={verdict.presentAs} />
          <button
            onClick={onRefresh}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="flex items-start gap-2.5 text-[15px] leading-relaxed text-slate-700 dark:text-slate-200 max-w-[74ch]">
          <ToneIcon className={`w-[18px] h-[18px] mt-0.5 shrink-0 ${TONE_CLASS[verdict.tone]}`} aria-hidden="true" />
          <span>{verdict.headline}</span>
        </p>
        {dayTotal && (
          <p className="max-w-[90ch] pl-7 text-[13px] text-slate-600 dark:text-slate-300">
            {dayTotal.map((phrase, index) =>
              phrase.figure ? (
                <span key={index} className="font-medium tabular-nums text-slate-700 dark:text-slate-200">
                  {phrase.text}
                </span>
              ) : (
                phrase.text
              ),
            )}
          </p>
        )}
      </div>

      {verdict.guidance && (
        <p className="max-w-[78ch] rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="font-semibold">What to check first. </span>
          {verdict.guidance}
        </p>
      )}

      {(canResume || canCancel) && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {canResume && (
              <button
                onClick={onResume}
                disabled={actioning || resumeBlocked}
                aria-describedby={resumeBlocked && resumeNote ? resumeNoteId : undefined}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actioning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Resume
              </button>
            )}
            {domainHeld && domain && domainHref && (
              <a
                href={domainHref}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Open {domain.Domain}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            )}
            {canCancel && (
              <button
                onClick={onCancel}
                disabled={actioning}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                {actioning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Cancel blast
              </button>
            )}
          </div>
          {resumeNote && (
            <p id={resumeNoteId} className="max-w-[78ch] text-[13px] text-slate-600 dark:text-slate-300">
              {resumeNote}
            </p>
          )}
          {canResume && resumeRefusal && (
            <div
              role="alert"
              className="flex max-w-[78ch] items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <div className="space-y-1">
                {resumeRefusal.map(message => (
                  <p key={message}>{message}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <BlastProgressBar blast={blast} verdict={verdict} />
        {multiDay && <BroadcastDayPlan days={verdict.days} recipients={verdict.recipients} />}
        {multiDay && domain && plan && (
          <p className="flex max-w-[90ch] items-start gap-2 pt-2 text-[13px] text-slate-600 dark:text-slate-300">
            <Info className="mt-[3px] h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" aria-hidden="true" />
            <span>
              {forecastAssumption(domain.Domain, plan.RateLimits)}
              {domainHref && (
                <>
                  {' '}
                  <a
                    href={domainHref}
                    className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline dark:text-primary-400"
                  >
                    Open {domain.Domain}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </>
              )}
            </span>
          </p>
        )}
        {domainStopped && domain && (
          <p className="flex items-center gap-1.5 pt-1 text-xs text-slate-500 dark:text-slate-400">
            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Emails to people who registered keep sending from {domain.Domain} while it's stopped.
          </p>
        )}
        {cancelledLate && (
          <p className="flex items-center gap-1.5 pt-1 text-xs text-slate-500 dark:text-slate-400">
            <CalendarX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {cancelledLate}
          </p>
        )}
      </div>

      {!verdict.notStartedYet && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <RateMeter
            label="Hard bounces"
            value={blast.Stats.HardBouncePercent}
            max={blast.MaxHardBouncePercent}
            isFinal={isTerminal}
            sample={tooEarlyToJudge
              ? `too early to judge — ${verdict.sent.toLocaleString()} of the ${blast.MinCanarySendsForRelease.toLocaleString()} sends needed`
              : sampleCaption}
          />
          <RateMeter
            label="Spam complaints"
            value={blast.Stats.ComplaintPercent}
            max={blast.MaxComplaintPercent}
            isFinal={isTerminal}
            sample={tooEarlyToJudge
              ? `too early to judge — ${verdict.sent.toLocaleString()} of the ${blast.MinCanarySendsForRelease.toLocaleString()} sends needed`
              : sampleCaption}
          />
          <RateMeter
            label="Delivered"
            value={blast.Stats.DeliveredPercent}
            max={blast.MinCanaryDeliveryPercent}
            direction="floor"
            isFinal={isTerminal}
            sample={sampleCaption}
            notApplicable={deliveryGateApplies ? undefined : 'this gate only runs on the test batch'}
          />
        </div>
      )}

      {pacing && <p className="text-[13px] text-slate-600 dark:text-slate-300">{pacing}</p>}

      <BlastFreshness blast={blast} onRefresh={onRefresh} />
    </section>
  )
}
